// 組織結構的小對話框：建 / 改名節點、〔＋ 戶〕（o30）、移動目標挑選、批次指定管理人（o31）、層級名稱設定（o05）。
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../../components/Dialog';
import { isOrgAdmin, type MyOrg } from '../../lib/planeApi';
import { BUILTIN_LEVELS, treeApi, type NodeAccess, type OrgNode, type OrgTreeItem, type TreeSettings } from '../../lib/orgTreeApi';
import { buildLevelsPut, defaultLevel, inList, isBuiltin, levelList, levelText } from '../../lib/levels';
import { memberName } from '../people/MembersTab';
import { selfNotifyKey, useOrgMembers } from './hooks';
import { StructureTree, type SelNode } from './StructureTree';
import { otErr } from './otErr';

export function NodeDialog({
  org,
  settings,
  mode,
  parent,
  node,
  close,
  done,
}: {
  org: MyOrg;
  settings?: TreeSettings | null;
  mode: 'add-node' | 'rename';
  parent: SelNode;
  node?: OrgNode;
  close(): void;
  done(msg: string): void;
}) {
  const { t } = useTranslation();
  const list = levelList(settings);
  const [name, setName] = useState(mode === 'rename' ? (node?.name ?? '') : '');
  const [level, setLevel] = useState(mode === 'rename' ? (node?.level_label ?? list[0] ?? '') : defaultLevel(settings, parent.level_label));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const options = useMemo(() => (level && !list.includes(level) ? [...list, level] : list), [list, level]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (mode === 'add-node') {
        await treeApi.createNode(org, { parent_node_id: parent.node_id, name: name.trim(), level_label: level });
        done(t('ot.node_created', { name: name.trim() }));
      } else if (node) {
        const body: Parameters<typeof treeApi.updateNode>[2] = { expected_version: node.version };
        if (name.trim() !== node.name) body.name = name.trim();
        if (level !== node.level_label) body.level_label = level;
        await treeApi.updateNode(org, node.node_id, body);
        done(t('ot.node_saved'));
      }
      close();
    } catch (x) {
      setErr(otErr(t, x));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={mode === 'add-node' ? t('ot.add_child_of', { name: parent.name }) : t('ot.rename_title', { name: node?.name ?? '' })} onClose={close}>
      <form className="form" onSubmit={submit}>
        <label className="field">
          <span>{t('ot.node_name')}</span>
          <input className="input" required maxLength={64} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="field">
          <span>{t('ot.level')}</span>
          <select className="input" value={level} onChange={(e) => setLevel(e.target.value)} required>
            {options.map((l) => (
              <option key={l} value={l} disabled={!list.includes(l)}>
                {levelText(t, l)}
                {!list.includes(l) ? ` (${t('ot.not_in_list')})` : ''}
              </option>
            ))}
          </select>
          <span className="small muted">{t('ot.level_hint')}</span>
        </label>
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim() || !level}>
            {mode === 'add-node' ? t('ot.create') : t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** 〔＋ 戶〕（o30）：第一欄是管理人（只有 owner / org_admin 看得到）；建立者不會成為管理人；未指派也能先裝機。 */
export function HomeDialog({
  org,
  at,
  access,
  leaf,
  close,
  done,
  onAssign,
}: {
  org: MyOrg;
  at: { node_id: string; name: string; isRoot: boolean };
  access?: NodeAccess;
  leaf: string;
  close(): void;
  done(msg: string): void;
  onAssign(): void;
}) {
  const { t } = useTranslation();
  const admin = isOrgAdmin(org);
  const members = useOrgMembers(org, admin);
  const me = (members.data ?? []).find((m) => m.is_me);
  const [mgr, setMgr] = useState<'none' | 'member' | 'me'>('none');
  const [mgrId, setMgrId] = useState('');
  const [name, setName] = useState('');
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const summary = useMemo(() => {
    if (!access) return null;
    const by = new Map<string, number>();
    for (const a of access.inherited) by.set(a.from_node_name, (by.get(a.from_node_name) ?? 0) + (a.principal_kind === 'group' ? (a.member_count ?? 1) : 1));
    const own = access.assignments.reduce((s, a) => s + (a.principal_kind === 'group' ? (a.member_count ?? 1) : 1), 0);
    if (own) by.set(at.name, (by.get(at.name) ?? 0) + own);
    const parts = [...by.entries()].map(([k, v]) => t('ot.cover_part', { node: k, n: v }));
    for (const d of access.delegations) parts.push(t('ot.cover_dlg', { org: d.to_org_name }));
    return parts;
  }, [access, at.name, t]);
  const self = mgr === 'me' || (mgr === 'member' && mgrId === me?.user_id);
  const notify = selfNotifyKey(org, members.data);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const managerId = mgr === 'me' ? me?.user_id : mgr === 'member' ? mgrId : undefined;
      await treeApi.createHome(org, {
        display_name: name.trim(),
        ...(at.isRoot ? {} : { node_id: at.node_id }),
        ...(managerId ? { manager_user_id: managerId } : {}),
        ...(self && reason.trim() ? { reason: reason.trim() } : {}),
        timezone: tz || undefined,
      });
      done(t('ot.home_created', { name: name.trim(), leaf }));
      close();
    } catch (x) {
      setErr(otErr(t, x));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('ot.add_home_at', { leaf, name: at.name })} onClose={close} wide>
      <form className="form" onSubmit={submit} data-testid="add-home">
        {admin && (
          <fieldset className="field">
            <legend>{t('ot.manager')}</legend>
            <label className="row-inline">
              <input type="radio" name="ot-mgr" checked={mgr === 'none'} onChange={() => setMgr('none')} />
              {t('ot.manager_none')}
            </label>
            <label className="row-inline">
              <input type="radio" name="ot-mgr" checked={mgr === 'member'} onChange={() => setMgr('member')} />
              {t('ot.manager_member')}
            </label>
            {mgr === 'member' && (
              <select className="input" value={mgrId} onChange={(e) => setMgrId(e.target.value)} aria-label={t('ot.manager_member')}>
                <option value="">—</option>
                {(members.data ?? [])
                  .filter((m) => m.status !== 'suspended')
                  .map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {memberName(m)}
                    </option>
                  ))}
              </select>
            )}
            <label className="row-inline">
              <input type="radio" name="ot-mgr" checked={mgr === 'me'} onChange={() => setMgr('me')} />
              {t('ot.manager_me')}
            </label>
          </fieldset>
        )}
        <label className="field">
          <span>{t('ot.home_name', { leaf })}</span>
          <input className="input" required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>{t('org.timezone')}</span>
          <input className="input" value={tz} onChange={(e) => setTz(e.target.value)} placeholder="Asia/Taipei" />
        </label>
        <p className="ot-info small">{t('ot.creator_not_manager')}</p>
        {summary && summary.length > 0 && <p className="small" data-testid="cover-summary">{t('ot.cover_summary', { list: summary.join(t('common.list_sep')) })}</p>}
        {summary && summary.length === 0 && (
          <div className="ot-warn small" data-testid="no-one-covers">
            <p style={{ margin: 0 }}>{t('ot.no_one_covers')}</p>
            <div className="row" style={{ marginTop: 6 }}>
              <button type="button" className="btn btn-sm" onClick={onAssign}>
                {t('ot.add_assignment')}
              </button>
              {admin && (
                <button type="button" className="btn btn-sm" onClick={() => setMgr('me')}>
                  {t('ot.manager_me')}
                </button>
              )}
            </div>
            <p className="muted small" style={{ margin: '6px 0 0' }}>
              {t('ot.whole_org_hint')}
            </p>
          </div>
        )}
        {mgr === 'none' && <p className="ot-info small" data-testid="unassigned-install">{t('ot.unassigned_install')}</p>}
        {self && (
          <>
            <p className="ot-warn small">
              {t('ot.self_manager_yellow', { leaf })} {t('ot.self_notify_' + notify)}
            </p>
            <label className="field">
              <span>{t('ot.reason')} *</span>
              <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} aria-label={t('ot.reason')} />
            </label>
          </>
        )}
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim() || (mgr === 'member' && !mgrId) || (self && !reason.trim())}>
            {t('ot.create_home', { leaf })}
            {self ? t('ot.reauth_suffix') : ''}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function PickNodeDialog({ org, title, exclude, close, pick }: { org: MyOrg; title: string; exclude?: string; close(): void; pick(n: SelNode): void }) {
  const { t } = useTranslation();
  const [sel, setSel] = useState<SelNode | null>(null);
  return (
    <Dialog open title={title} onClose={close}>
      <p className="muted small">{t('ot.pick_target_hint')}</p>
      <StructureTree org={org} selected={sel?.node_id} onSelect={setSel} showLock={false} showUnassigned={false} pick={{ exclude }} />
      <div className="dialog-actions">
        <button className="btn" onClick={close}>
          {t('common.cancel')}
        </button>
        <button className="btn btn-primary" disabled={!sel || sel.node_id === exclude} onClick={() => sel && pick(sel)}>
          {t('ot.next_preview')}
        </button>
      </div>
    </Dialog>
  );
}

/** 批次指定管理人（o31）：只收未指派的戶、≤ 500、理由必填、全部成功或全部不動；選自己 = 一次自我授權。 */
export function BatchManagerDialog({ org, homes, close, done }: { org: MyOrg; homes: OrgTreeItem[]; close(): void; done(msg: string): void }) {
  const { t } = useTranslation();
  const members = useOrgMembers(org);
  const [who, setWho] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => (globalThis.crypto?.randomUUID?.() ?? String(Date.now())));
  const target = (members.data ?? []).find((m) => m.user_id === who);
  const self = !!target?.is_me;
  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await treeApi.assignManagers(
        org,
        { home_ids: homes.map((h) => h.home_id!), target_user_id: who, expected_versions: Object.fromEntries(homes.map((h) => [h.home_id!, h.version])), reason: reason.trim() },
        key,
      );
      done(t('ot.managers_done', { name: memberName(target), count: homes.length }));
      close();
    } catch (x) {
      setErr(otErr(t, x));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('ot.manager_dialog_title', { count: homes.length })} onClose={close} wide>
      <div className="form" data-testid="batch-manager">
        <p className="small muted">{homes.map((h) => h.name).join('、')}</p>
        <label className="field">
          <span>{t('ot.manager_pick')}</span>
          <select className="input" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">—</option>
            {(members.data ?? [])
              .filter((m) => m.status !== 'suspended')
              .map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {memberName(m)}
                  {m.is_me ? ` (${t('ot.you')})` : ''}
                </option>
              ))}
          </select>
        </label>
        {target && <p className="small">{t('ot.manager_effect', { name: memberName(target), count: homes.length })}</p>}
        <p className="ot-info small">{t('ot.all_or_nothing')}</p>
        {self && <p className="ot-warn small">{t('ot.self_manager_batch', { count: homes.length })}</p>}
        <label className="field">
          <span>{t('ot.reason')} *</span>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} aria-label={t('ot.reason')} />
        </label>
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy || !who || !reason.trim()} onClick={() => void submit()}>
            {t('ot.manager_submit', { count: homes.length })}
            {t('ot.reauth_suffix')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** 層級名稱設定（o05）：清單 ≤ 12 個、每個 1–20 字；從內建改成自訂時每列選「原本的內建名稱」（from_builtin）。 */
export function LevelSettingsDialog({ org, settings, close, done }: { org: MyOrg; settings: TreeSettings | null; close(): void; done(msg: string): void }) {
  const { t } = useTranslation();
  const builtin = settings?.levels == null;
  const [rows, setRows] = useState<{ text: string; from?: string | null }[]>([]);
  const [leaf, setLeaf] = useState(settings?.leaf_label ?? '');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRows(builtin ? BUILTIN_LEVELS.map((k) => ({ text: levelText(t, k), from: k })) : (settings?.levels ?? []).map((x) => ({ text: x })));
    // 打開時一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const previewPath = [org.display_name, ...rows.filter((r) => r.text.trim()).map((r) => `…〔${r.text.trim()}〕`), `…〔${leaf.trim() || t('ot.leaf_default')}〕`].join(' › ');
  async function save() {
    const r = buildLevelsPut(settings, rows, leaf);
    if (!r.ok) return setErr(t('ot.levels_err_' + r.reason));
    setBusy(true);
    setErr(null);
    try {
      await treeApi.putSettings(org, { ...r.body, ...(settings?.version !== undefined ? { expected_version: settings.version } : {}) });
      done(t('ot.levels_saved'));
      close();
    } catch (x) {
      setErr(otErr(t, x));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('ot.level_settings')} onClose={close} wide>
      <div className="form" data-testid="level-settings">
        <p className="muted small">{t('ot.levels_hint')}</p>
        <ul className="plain-list">
          {rows.map((r, i) => (
            <li key={i} className="row ot-level-row">
              <input
                className="input"
                style={{ maxWidth: 200 }}
                value={r.text}
                maxLength={20}
                onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                aria-label={t('ot.level_n', { n: i + 1 })}
              />
              {builtin && (
                <label className="row-inline small">
                  {t('ot.from_builtin')}
                  <select className="input" style={{ width: 'auto' }} value={r.from ?? ''} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, from: e.target.value || null } : x)))}>
                    <option value="">—</option>
                    {BUILTIN_LEVELS.map((k) => (
                      <option key={k} value={k}>
                        {levelText(t, k)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {!builtin && !inList(settings, r.text) && isBuiltin(r.text) && <span className="badge">{t('ot.not_in_list')}</span>}
              <button className="icon-btn" aria-label={t('common.action_remove')} onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                ×
              </button>
            </li>
          ))}
        </ul>
        <button className="btn btn-sm" disabled={rows.length >= 12} onClick={() => setRows([...rows, { text: '' }])}>
          {t('ot.add_level')}
        </button>
        <label className="field">
          <span>{t('ot.leaf_label')}</span>
          <input className="input" style={{ maxWidth: 200 }} maxLength={20} value={leaf} onChange={(e) => setLeaf(e.target.value)} placeholder={t('ot.leaf_default')} />
        </label>
        <p className="small">
          {t('ot.levels_preview')}
          {t('common.colon')}
          {previewPath}
        </p>
        <p className="muted small">{t('ot.levels_no_perm')}</p>
        {builtin && <p className="ot-warn small">{t('ot.levels_builtin_warn')}</p>}
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {t('common.save')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
